import { Columns } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { Dropdown, DropdownButton, DropdownContent } from "@aviary";
import { OpenAction } from "@aviary/components/Dropdown";
import {
  useWordpressFeaturedItemsQuery,
  FeaturedBrandLocation,
} from "@emerson/data/queries/WordpressFeaturedItemsQuery/WordpressFeaturedItemsQuery.query";
import { l } from "@emerson/locales/i18n";
import type { BrandSplinter } from "@shared/data/queries/BrandsQuery/Brands.query";
import { useBrandsQuery } from "@shared/data/queries/BrandsQuery/Brands.query";
import { isE2E } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import * as navStyles from "../Navigation/Navigation.styles";

import { FeaturedBrands } from "./FeaturedBrands/FeaturedBrands";
import { PopularBrands } from "./PopularBrands/PopularBrands";

import * as styles from "./BrandsDropdown.styles";

const DROPDOWN_NAME = "Brands";

type Props = {
  toggleCallback: (dropdown: string) => (isOpen: boolean) => void;
  openDropdown: string;
};

const BrandsDropdown = ({ toggleCallback, openDropdown }: Props) => {
  const { t } = useTranslation();
  const {
    loading: loadingCoopFeaturedBrands,
    error: coopFeaturedBrandsError,
    data: coopFeaturedBrands,
  } = useWordpressFeaturedItemsQuery({
    variables: { type: "brand" },
    skip: isE2E(),
  });

  const { loading: loadingBrands, data } = useBrandsQuery({
    variables: {
      first: 16,
      filters: { active: true, list: "EE_TOP_BRANDS" },
    },
  });

  const brands = data?.viewer?.brands?.nodes || [];
  const currentBrands = [
    "Allergy Research Group",
    "Designs for Health",
    "Microbiome Labs",
    "Thorne",
  ];

  let featuredBrands = [...brands].filter(brand => currentBrands.includes(brand.name));

  if (!coopFeaturedBrandsError) {
    const coopBrands = coopFeaturedBrands?.featuredItems?.nodes
      ?.filter(node => node.featuredBrandLocation === FeaturedBrandLocation.megamenu)
      .slice(0, 4)
      .map(item => {
        return {
          id: item.featuredBrandId,
          logo: item.featuredBrandLogo,
          name: item.featuredBrandName,
        } as BrandSplinter;
      });

    if (coopBrands && coopBrands.length > 0) {
      featuredBrands = coopBrands;
    }
  }

  const sortedBrands = [...brands].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dropdown
      openAction={OpenAction.HOVER}
      isFullHeight
      dropdownPlacement="bottom"
      isOpen={openDropdown === DROPDOWN_NAME}
      toggleCallback={toggleCallback(DROPDOWN_NAME)}
    >
      <div css={navStyles.dropdownButtonBorder}>
        <DropdownButton isText hasIcon={false} css={navStyles.dropdownButton}>
          {t(l.header.Brands.Header)}
        </DropdownButton>
      </div>
      <DropdownContent css={[styles.BrandsDropdownContent, navStyles.dropdownContent]}>
        <Columns customCss={styles.BrandContent}>
          <Columns.Column columnWidth={4}>
            <FeaturedBrands
              isLoading={!!loadingBrands && loadingCoopFeaturedBrands}
              brands={featuredBrands}
            />
          </Columns.Column>
          <Columns.Column columnWidth={8}>
            <PopularBrands
              isLoading={!!loadingBrands && loadingCoopFeaturedBrands}
              brands={sortedBrands}
            />
          </Columns.Column>
        </Columns>
      </DropdownContent>
    </Dropdown>
  );
};
export { BrandsDropdown };
